import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Engineering,
  Speed,
  Warning,
  TrendingUp,
  Assessment,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User, Asset, Component, Alert as AlertType } from '../types';
import { Socket } from 'socket.io-client';

interface DashboardProps {
  user: User;
  socket: Socket | null;
}

interface DashboardStats {
  totalAssets: number;
  totalComponents: number;
  healthyComponents: number;
  warningComponents: number;
  criticalComponents: number;
  avgHealthIndex: number;
}

const Dashboard: React.FC<DashboardProps> = ({ user, socket }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalAssets: 0,
    totalComponents: 0,
    healthyComponents: 0,
    warningComponents: 0,
    criticalComponents: 0,
    avgHealthIndex: 0,
  });
  const [recentAssets, setRecentAssets] = useState<Asset[]>([]);
  const [alerts, setAlerts] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    
    if (socket) {
      socket.on('health_update', handleHealthUpdate);
      socket.on('alert', handleAlert);
    }

    return () => {
      if (socket) {
        socket.off('health_update', handleHealthUpdate);
        socket.off('alert', handleAlert);
      }
    };
  }, [socket]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Mock data - in production, this would come from API calls
      setStats({
        totalAssets: 12,
        totalComponents: 156,
        healthyComponents: 120,
        warningComponents: 28,
        criticalComponents: 8,
        avgHealthIndex: 78.5,
      });

      setRecentAssets([
        {
          id: '1',
          name: 'My Rotator 6',
          type: 'Rotator',
          specifications: {},
          organizationId: user.organizationId,
          organizationName: 'Default Organization',
          status: 'active',
          componentCount: 8,
          avgHealthIndex: 85.2,
          createdAt: '2025-10-01T10:00:00Z',
          updatedAt: '2025-10-04T10:00:00Z',
        },
        {
          id: '2',
          name: 'Bridge XYZ',
          type: 'Bridge',
          specifications: {},
          organizationId: user.organizationId,
          organizationName: 'Default Organization',
          status: 'active',
          componentCount: 15,
          avgHealthIndex: 72.1,
          createdAt: '2025-09-15T10:00:00Z',
          updatedAt: '2025-10-04T09:30:00Z',
        },
      ]);

      setAlerts([
        {
          id: '1',
          componentId: 'comp-1',
          componentName: 'Front Bearing',
          sensorId: 'sensor-1',
          sensorName: 'Vibration Sensor',
          sensorType: 'vibration',
          value: 8.5,
          unit: 'mm/s',
          alertLevel: 'warning',
          message: 'Vibration reading 8.5 mm/s is above normal range',
          timestamp: '2025-10-04T10:15:00Z',
        },
        {
          id: '2',
          componentId: 'comp-2',
          componentName: 'Motor',
          sensorId: 'sensor-2',
          sensorName: 'Temperature Sensor',
          sensorType: 'temperature',
          value: 95.2,
          unit: '°C',
          alertLevel: 'critical',
          message: 'Temperature reading 95.2°C is critical',
          timestamp: '2025-10-04T09:45:00Z',
        },
      ]);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHealthUpdate = (data: any) => {
    console.log('Health update received:', data);
    // Update stats based on real-time health updates
  };

  const handleAlert = (alert: AlertType) => {
    setAlerts(prev => [alert, ...prev.slice(0, 9)]); // Keep only latest 10 alerts
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'success';
      case 'caution': return 'warning';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getAlertSeverity = (level: string) => {
    return level === 'critical' ? 'error' : 'warning';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Welcome back, {user.firstName || user.username}!
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Engineering color="primary" sx={{ mr: 2 }} />
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Total Assets
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalAssets}
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Speed color="primary" sx={{ mr: 2 }} />
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Components
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalComponents}
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: "flex", alignItems: "center" }}>
                <TrendingUp color="primary" sx={{ mr: 2 }} />
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Avg Health
                  </Typography>
                  <Typography variant="h4">
                    {stats.avgHealthIndex.toFixed(1)}%
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Warning color="primary" sx={{ mr: 2 }} />
                <div>
                  <Typography color="textSecondary" gutterBottom>
                    Critical
                  </Typography>
                  <Typography variant="h4" color="error">
                    {stats.criticalComponents}
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Assets */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Assets
              </Typography>
              {recentAssets.map((asset) => (
                <Paper
                  key={asset.id}
                  sx={{
                    p: 2,
                    mb: 2,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => navigate(`/asset/${asset.id}`)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <Typography variant="h6">{asset.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {asset.type} • {asset.componentCount} components
                      </Typography>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Typography variant="h6" color="primary">
                        {asset.avgHealthIndex?.toFixed(1)}%
                      </Typography>
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                      />
                    </div>
                  </div>
                </Paper>
              ))}
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/assets')}
                sx={{ mt: 2 }}
              >
                View All Assets
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Alerts */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Alerts
              </Typography>
              {alerts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No recent alerts
                </Typography>
              ) : (
                alerts.map((alert) => (
                  <Alert
                    key={alert.id}
                    severity={getAlertSeverity(alert.alertLevel)}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2">
                      <strong>{alert.componentName}</strong>
                    </Typography>
                    <Typography variant="caption">
                      {alert.message}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {new Date(alert.timestamp).toLocaleString()}
                    </Typography>
                  </Alert>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;





